import React, { useCallback, useMemo, useState } from 'react';
import DataGrid, { Column, Scrolling, SearchPanel, Editing, Lookup } from 'devextreme-react/data-grid';
import notify from 'devextreme/ui/notify';
import competitionEntityApi from '../../../api/competitionEntityApi';

export function CompetitionPermissions({ competitionId, data, users, accessToken, onEntityChange }) {
  const [isSaving, setIsSaving] = useState(false);

  const rows = useMemo(() => {
    if (data && Array.isArray(data)) {
      return data.map((permission) => ({
        competitonPermissionId: permission.competitonPermissionId,
        userId: permission.userId,
        competititionId: permission.competititionId,
        enabled: permission.enabled,
        readOnly: permission.readOnly,
        editSessions: permission.editSessions,
        editEvents: permission.editEvents,
        editAthletes: permission.editAthletes,
        editHeats: permission.editHeats,
        editClubs: permission.editClubs,
        editEntries: permission.editEntries,
        editRelays: permission.editRelays,
        editResults: permission.editResults,
        importEntries: permission.importEntries,
        importResults: permission.importResults
      }));
    }
    return [];
  }, [data]);

  const handleSaving = useCallback(async (e) => {
    if (!e.changes?.length) {
      return;
    }

    if (!accessToken) {
      notify('Kein Zugriffstoken verfügbar. Bitte erneut anmelden.', 'warning', 3000);
      e.cancel = true;
      return;
    }

    e.cancel = true;
    setIsSaving(true);

    try {
      for (const change of e.changes) {
        if (change.type === 'insert') {
          const payload = { ...change.data, competititionId: competitionId };
          const saved = await competitionEntityApi.competitionPermissions.insert(competitionId, payload, accessToken);
          onEntityChange?.('CompetitionPermission', 'insert', saved);
        }

        if (change.type === 'update') {
          const current = (data || []).find((item) => item.competitonPermissionId === change.key) || {};
          const payload = { ...current, ...change.data, competitonPermissionId: change.key, competititionId: competitionId };
          const saved = await competitionEntityApi.competitionPermissions.update(competitionId, change.key, payload, accessToken);
          onEntityChange?.('CompetitionPermission', 'update', saved);
        }

        if (change.type === 'remove') {
          await competitionEntityApi.competitionPermissions.remove(competitionId, change.key, accessToken);
          onEntityChange?.('CompetitionPermission', 'delete', { competitonPermissionId: change.key });
        }
      }

      e.component.cancelEditData();
    } catch (err) {
      const message = err?.response?.data || err?.message || 'Änderung konnte nicht gespeichert werden';
      notify(message, 'error', 4000);
    } finally {
      setIsSaving(false);
    }
  }, [accessToken, competitionId, data, onEntityChange]);

  return (
    <div className='competition-section' style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className='section-header'>
        <div>
          <div className='section-title'>Berechtigungen</div>
          <div className='section-subtitle'>Benutzerzugriffe und Bearbeitungsrechte</div>
        </div>
        <div className='section-pill subtle'>{rows.length} Berechtigung{rows.length !== 1 ? 'en' : ''}</div>
      </div>

      <DataGrid
        className='grid theme-dependent'
        dataSource={rows}
        keyExpr='competitonPermissionId'
        columnAutoWidth
        rowAlternationEnabled
        hoverStateEnabled
        showBorders={false}
        repaintChangesOnly
        onSaving={handleSaving}
        loadPanel={{ enabled: isSaving, text: 'Speichern...' }}
        style={{ flex: 1 }}
      >
        <SearchPanel visible placeholder='Suche nach User ID...' />
        <Column dataField='userId' caption='User ID' width={280}>
          <Lookup
            dataSource={users}
            valueExpr='userId'
            displayExpr='displayName'
          />
        </Column>
        <Column dataField='enabled' caption='Aktiviert' dataType='boolean' width={100} />
        <Column dataField='readOnly' caption='Nur Lesen' dataType='boolean' width={100} />
        <Column dataField='editSessions' caption='Abschnitte' dataType='boolean' width={110} />
        <Column dataField='editEvents' caption='Wettkämpfe' dataType='boolean' width={120} />
        <Column dataField='editAthletes' caption='Athleten' dataType='boolean' width={100} />
        <Column dataField='editHeats' caption='Läufe' dataType='boolean' width={90} />
        <Column dataField='editClubs' caption='Vereine' dataType='boolean' width={100} />
        <Column dataField='editEntries' caption='Meldungen' dataType='boolean' width={110} />
        <Column dataField='editRelays' caption='Staffeln' dataType='boolean' width={100} />
        <Column dataField='editResults' caption='Ergebnisse' dataType='boolean' width={110} />
        <Column dataField='importEntries' caption='Import Meld.' dataType='boolean' width={120} />
        <Column dataField='importResults' caption='Import Erg.' dataType='boolean' width={110} />
        <Editing
          mode='popup'
          allowAdding
          allowUpdating
          allowDeleting
          useIcons
        />
        <Scrolling mode='virtual' />
      </DataGrid>
    </div>
  );
}
